import { useCallback, useContext, useMemo } from 'react';
import { ModerationContext } from '@/providers/ModerationProvider';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { ImageDimension } from '@/type/detection.types';

type BoxCoordinatesProps = {
  isModerated?: boolean;
  coordinates?: TResultImageCoordinatesSchema;
  imageDimension?: ImageDimension;
};

type PolygonPointsProps = {
  coordinates?: TResultImageCoordinatesSchema;
};

type DenormalizePolygonPointsProps = {
  originalBoundingBox?: Array<number>;
  coordinates?: TResultImageCoordinatesSchema;
  imageDimension?: ImageDimension;
};

type PolygonScalePointsProps = {
  isModerated?: boolean;
  polygonPoints?: { x: number; y: number }[];
  denormalizePolygonPoints?: () =>
    | { elNumber?: string; point?: { x?: number; y?: number }[] }
    | undefined;
};

export const useModerate = () => {
  const context = useContext(ModerationContext);

  if (!context) {
    throw new Error('useModerate must be used within an ModerateProvider');
  }

  return context;
};

export const useEngineCorrection = () => {
  const context = useContext(EngineCorrectionContext);

  if (!context) {
    throw new Error(
      'useModerate must be used within an EngineCorrectionProvider',
    );
  }

  return context;
};

export const useBoxCoordinates = ({
  isModerated,
  coordinates,
  imageDimension,
}: BoxCoordinatesProps) => {
  const getCoordinates = useCallback(() => {
    if (
      coordinates?.xCenter == null ||
      coordinates?.yCenter == null ||
      coordinates?.width == null ||
      coordinates?.height == null
    ) {
      return null;
    }

    let x = 0;
    let y = 0;
    let width = 0;
    let height = 0;

    if (isModerated) {
      x = coordinates?.xCenter * 100 - (coordinates?.width * 100) / 2;
      y = coordinates?.yCenter * 100 - (coordinates?.height * 100) / 2;
      width = coordinates?.width * 100;
      height = coordinates?.height * 100;
    } else {
      x = (coordinates?.xCenter / imageDimension?.width) * 100;
      y = (coordinates?.yCenter / imageDimension?.height) * 100;

      const actualWidth = coordinates?.width - coordinates?.xCenter;
      const actualHeight = Math.abs(coordinates?.height - coordinates?.yCenter);
      width = (actualWidth / imageDimension?.width) * 100;
      height = (actualHeight / imageDimension?.height) * 100;
    }

    return {
      x,
      y,
      width,
      height,
    };
  }, [isModerated, coordinates, imageDimension]);

  return { getCoordinates };
};

export const usePolygonPoints = ({ coordinates }: PolygonPointsProps) => {
  const getPoints = useMemo(() => {
    if (
      !coordinates?.point?.length ||
      !Array.isArray(coordinates?.point) ||
      coordinates?.point?.length === 0
    )
      return null;

    return coordinates?.point?.map((point) => ({
      x: point?.x * 100,
      y: point?.y * 100,
    }));
  }, [coordinates?.point]);

  return { getPoints };
};

export const useDenormalizePolygonPoints = ({
  originalBoundingBox,
  coordinates,
  imageDimension,
}: DenormalizePolygonPointsProps) => {
  const getDenormalizedCoordinates = useCallback(() => {
    if (!coordinates) return undefined;

    const bbox = originalBoundingBox;
    const imgDim = imageDimension;

    if (bbox && bbox?.length >= 4) {
      // Wait for image dimensions before denormalizing
      if (!imgDim) return undefined;

      const [x1, y1, x2, y2] = bbox;
      const bboxW = x2 - x1;
      const bboxH = y2 - y1;

      return {
        elNumber: coordinates?.elNumber,
        point: coordinates?.point?.map((p) => ({
          x: (x1 + p?.x * bboxW) / imgDim?.width,
          y: (y1 + p?.y * bboxH) / imgDim?.height,
        })),
      };
    }

    return coordinates;
  }, [coordinates, originalBoundingBox, imageDimension]);

  return { getDenormalizedCoordinates };
};

export const usePolygonScalePoints = ({
  isModerated,
  polygonPoints,
  denormalizePolygonPoints,
}: PolygonScalePointsProps) => {
  const getScalePoints = useCallback(
    (canvas) => {
      if (isModerated) {
        return polygonPoints?.map((point) => ({
          x: (point?.x / 100) * canvas?.width,
          y: (point?.y / 100) * canvas?.height,
        }));
      } else {
        const denormalized = denormalizePolygonPoints();
        return denormalized?.point?.map((point) => ({
          x: point?.x * canvas?.width,
          y: point?.y * canvas?.height,
        }));
      }
    },
    [isModerated, polygonPoints, denormalizePolygonPoints],
  );

  return { getScalePoints };
};
